import convert from "convert-units";
import {
  chain as lodashChain,
  cloneDeep as lodashCloneDeep,
  defaults as lodashDefaults,
  find as lodashFind,
  get as lodashGet,
  round as lodashRound,
} from "lodash";
import moment from "moment";
import React from "react";
import { withTranslation } from "react-i18next";
import submit from "submit-form";
import CalendarAddExerciseModal from "../../../parts/modals/calendar-add-exercise";
import JWPlayerVideoModal from "../../../parts/modals/jwplayer-video";
import ProgressionModal from "../../../parts/modals/progression";
import ShareModal from "../../../parts/modals/share";
import SwapExerciseModal from "../../../parts/modals/swap-exercise";
import SvgIcon from "../../../parts/SvgIcon";
import { RillContext } from "../../../rill/RillReact/share/base";
import { formattedTarget } from "../../../utils/actuals";
import { getActiveDayStats } from "../../../utils/daily-stats";
import getDayRangeString from "../../../utils/day-range-string";
import {
  checkOtherExercise,
  validateSet,
} from "../../../utils/exercise-validations";
import getSessionOrder from "../../../utils/get-session-order";
import {
  buildVideoModalUrl,
  findVideoNeighbors,
} from "../../../utils/video-modal-url";
import PhotoModal from "../../athlete-portal/calendar/progress-photos/PhotoModal";
import Picture from "../../athlete-portal/calendar/progress-photos/Picture";
import QuickMetrics from "./components/QuickMetrics";
import SingleDayCycleBar from "./components/SingleDayCycleBar";
import TopBarSingleDay from "./components/TopBarSingleDay";
import DaylyWorkoutHeader from "./DaylyWorkoutHeader";
import ExerciseFrame from "./ExerciseFrame";

// Constants
const {
  DATE_FORMATS: { DATE_DAY_FORMAT, UTC },
  S3,
  PRIMARIESWITHOLYMPIC,
} = require("../../../constants");

class View extends React.Component {
  constructor(props, context) {
    super(props);

    const { activeUser } = context.locals;
    this.user = activeUser;
    this.state = {
      notes: "",
      openQuickEditNotes: false,
      openQuickEdit: false,
      exercises: [],
      sendToCoach: false,
      photoModal: {
        open: false,
        type: "",
        pictureImg: "",
      },
    };
    this.loadInputs = [];
    this.intensityInputs = [];

    this.handleDuplicateExercise = this.handleDuplicateExercise.bind(this);
    this.openQuickEditMode = this.openQuickEditMode.bind(this);
    this.handleQuickEditChange = this.handleQuickEditChange.bind(this);
    this.handleAddSetLocally = this.handleAddSetLocally.bind(this);
    this.saveQuickEdit = this.saveQuickEdit.bind(this);
    this.closeQuickEditMode = this.closeQuickEditMode.bind(this);
    this.toggleModal = this.toggleModal.bind(this);
  }

  toggleModal(pictureImg = "", type = "") {
    this.setState({
      photoModal: {
        open: !this.state.photoModal.open,
        type,
        pictureImg: S3.toCDN(pictureImg),
      },
    });
  }

  handleDuplicateExercise(body) {
    submit("/actions/exercise/duplicate-from-single-day", {
      method: "POST",
      body,
    });
  }

  openQuickEditMode(e) {
    e.preventDefault();
    this.setState((prevState) =>
      Object.assign({}, prevState, { openQuickEdit: true })
    );
  }

  closeQuickEditMode(e) {
    e?.preventDefault();
    document.getElementById("quick-edit").reset();
    this.setState((prevState) =>
      Object.assign({}, prevState, { openQuickEdit: false })
    );
  }

  handleQuickEditChange({ target: { name, value } }, exId, setId) {
    const { locals } = this.context;
    const exercises = lodashCloneDeep(this.state.exercises);
    const exerciseIndex = exercises.findIndex((ex) => ex._id === exId);
    const exercise =
      exercises[exerciseIndex] ||
      lodashCloneDeep(lodashGet(this.context.locals, "editExercises", [])).find(
        (ex) => ex._id === exId
      ) ||
      lodashCloneDeep(
        lodashGet(this.context.locals, "activeExercises", [])
      ).find((ex) => ex._id === exId) ||
      {};

    const set = lodashGet(exercise, ["sets", setId], false);

    if (set && set[name] !== value && name !== "exerciseType") {
      set[name] = value;
      const primary = lodashGet(exercise, "primary");
      const repMax =
        exercise.type !== "secondary"
          ? lodashGet(
              exercise,
              `repMaximums.${primary}`,
              lodashGet(
                this.context.locals.activeUser,
                `repMaximums.${primary}`,
                0
              )
            )
          : exercise.secondaryRepMaximum;

      // ensure load and percentRepMax are in sync
      if (name === "load") {
        if (this.context.locals.activeUser.units === "imperial")
          set.load = lodashRound(convert(value).from("lb").to("kg"), 2);
        if (exercise.type !== "assistant")
          set.percentRepMax = lodashRound((set.load / repMax) * 100, 1);
      }

      if (name === "percentRepMax")
        set.load = lodashRound((repMax * set.percentRepMax) / 100, 2);

      const loadInput = this.loadInputs[`${exId}_${setId}`];
      if (loadInput) {
        if (name === "percentRepMax") {
          let loadToShow = set.load;
          if (this.context.locals.activeUser.units === "imperial") {
            loadToShow = lodashRound(
              convert(loadToShow).from("kg").to("lb"),
              2
            );
          }
          loadInput.value = loadToShow;
        }
        if (!this.intensityInputs[`${exId}_${setId}`]) {
          this.intensityInputs[`${exId}_${setId}`] = { value: 0 };
        }
        this.intensityInputs[`${exId}_${setId}`].value = set.percentRepMax ?? 0;
      }

      const validatedSet = validateSet({
        exercise,
        set,
        locals,
        field: name,
      });
      if (validatedSet) {
        exercise.sets[setId] = validatedSet;

        this.setExersicesStates(exercise, exercises, exerciseIndex);
      } else {
        // revert to original value
        const originalSet = lodashCloneDeep(
          lodashGet(this.context.locals, "activeExercises", [])
        ).find((ex) => ex._id === exId)?.sets?.[setId];
        document.getElementById(`${exId}_${setId}_${name}`).value =
          originalSet?.[name] ?? "";
      }
    } else if (name === "notes") {
      exercise.notes = value === "" ? " " : value;

      this.setExersicesStates(exercise, exercises, exerciseIndex);
    } else if (name === "repsPerSide" || name === "tempo") {
      exercise[name] = value;

      this.setExersicesStates(exercise, exercises, exerciseIndex);
    } else if (name === "exerciseType") {
      const { movement, repMaximum, newPrimary } = value;
      const sets = lodashGet(exercise, "sets", false);

      // if the movement chosen is a primary, change to a primary
      if (PRIMARIESWITHOLYMPIC.includes(movement)) {
        // setting new values
        exercise.type = "primary";
        exercise.primary = movement;
        // resetting secondary and secondary RM
        exercise.secondary = "null";
        exercise.secondaryRepMaximum = 0;
        exercise.parameters = "null";
        // updating the values of each set in the exercise
        sets.forEach((currentSet, idx) => {
          if (
            currentSet.load &&
            currentSet.percentRepMax &&
            currentSet.load >= 0 &&
            currentSet.percentRepMax >= 0
          ) {
            currentSet.load = lodashRound(
              (repMaximum * currentSet.percentRepMax) / 100,
              2
            );
            currentSet.percentRepMax = lodashRound(
              (currentSet.load / repMaximum) * 100,
              1
            );
          }

          // updating the load shown
          const loadInput = this.loadInputs[`${exId}_${idx}`];
          if (loadInput) {
            let loadToShow = currentSet.load;
            const { units } = this.context.locals.activeUser;

            if (units === "imperial") {
              loadToShow = lodashRound(
                convert(loadToShow).from("kg").to("lb"),
                2
              );
            }
            loadInput.value = loadToShow;
          }
          // Making sure no values are invalid after any changes
          if (!currentSet.load || currentSet.load === null) currentSet.load = 0;
          if (!currentSet.percentRepMax || currentSet.percentRepMax === null)
            currentSet.percentRepMax = 0;
        });
      }
      // isAssistant = true then it is assistant
      else if (newPrimary === "assistant") {
        exercise.type = "assistant";
        exercise.primary = "assistant";
        exercise.secondary = movement;
        exercise.secondaryRepMaximum = 0;
        if (value.parameters) exercise.parameters = value.parameters;
      }
      // else its a secondary
      else {
        // change type from primary/assistant to secondary and set new values
        exercise.type = "secondary";
        exercise.primary = newPrimary;
        exercise.secondary = movement;
        exercise.secondaryRepMaximum = repMaximum;
        exercise.parameters = "null";
        // updating the values of each set in the exercise
        sets.forEach((currentSet, idx) => {
          if (
            currentSet.load &&
            currentSet.percentRepMax &&
            currentSet.load >= 0 &&
            currentSet.percentRepMax >= 0
          ) {
            currentSet.load = lodashRound(
              (repMaximum * currentSet.percentRepMax) / 100,
              2
            );
            currentSet.percentRepMax = lodashRound(
              (currentSet.load / repMaximum) * 100,
              1
            );
          }

          // updating the load shown
          const loadInput = this.loadInputs[`${exId}_${idx}`];
          if (loadInput) {
            let loadToShow = currentSet.load;
            const { units } = this.context.locals.activeUser;

            if (units === "imperial") {
              loadToShow = lodashRound(
                convert(loadToShow).from("kg").to("lb"),
                2
              );
            }
            loadInput.value = loadToShow;
          }
          // Making sure no values are invalid after any changes
          if (!currentSet.load || currentSet.load === null) currentSet.load = 0;
          if (!currentSet.percentRepMax || currentSet.percentRepMax === null)
            currentSet.percentRepMax = 0;
        });
      }

      this.setExersicesStates(exercise, exercises, exerciseIndex, true);
    }
  }

  // Adds a newly-entered set straight into the quick-edit local state and
  // auto-saves, instead of POSTing to /exercise/quick-edit/sets/add directly.
  // Mirrors EditorSessionTable's handleAddSetLocally.
  handleAddSetLocally(
    exId,
    setIndex,
    newSet,
    complexExerciseIds = [],
    repsTwo,
    repsThree
  ) {
    const exercises = lodashCloneDeep(this.state.exercises);
    const exerciseIndex = exercises.findIndex((ex) => ex._id === exId);
    const exercise =
      exercises[exerciseIndex] ||
      lodashCloneDeep(lodashGet(this.context.locals, "editExercises", [])).find(
        (ex) => ex._id === exId
      ) ||
      lodashCloneDeep(
        lodashGet(this.context.locals, "activeExercises", [])
      ).find((ex) => ex._id === exId) ||
      {};

    exercise.sets.splice(parseInt(setIndex, 10) + 1, 0, newSet);

    complexExerciseIds.forEach((complexExId) => {
      const complexIndex = exercises.findIndex((ex) => ex._id === complexExId);
      let complexExercise = exercises[complexIndex];

      if (!complexExercise) {
        complexExercise =
          lodashCloneDeep(
            lodashGet(this.context.locals, "editExercises", [])
          ).find((ex) => ex._id === complexExId) ||
          lodashCloneDeep(
            lodashGet(this.context.locals, "activeExercises", [])
          ).find((ex) => ex._id === complexExId);
        if (complexExercise) exercises.push(complexExercise);
      }

      if (complexExercise) {
        const complexSet = { ...newSet };
        if (complexExercise.indexInComplex === 1)
          complexSet.reps = repsTwo || newSet.reps;
        if (complexExercise.indexInComplex === 2)
          complexSet.reps = repsThree || newSet.reps;
        complexExercise.sets.splice(parseInt(setIndex, 10) + 1, 0, complexSet);
      }
    });

    this.setExersicesStates(exercise, exercises, exerciseIndex, true);
  }

  setExersicesStates(
    exercise,
    exercises,
    exerciseIndex,
    saveAfterChange = false
  ) {
    // if exercise already exists in state
    if (exerciseIndex > -1) exercises[exerciseIndex] = exercise;
    // otherwise push to exercises
    if (exerciseIndex < 0) exercises.push(exercise);

    this.setState(
      (prevState) => Object.assign({}, prevState, { exercises }),
      saveAfterChange
        ? () => this.saveQuickEdit({ preventDefault: () => {} }, false)
        : undefined
    );
  }

  saveQuickEdit(e, close = true) {
    e.preventDefault();
    // An unreadable RPE ("8-7") is never emitted into state, so state only
    // holds its last valid prefix ("8"). The cell's own error is the only
    // record of it.
    const rpeError = document
      .querySelector("#quick-edit [data-rpe-error]")
      ?.getAttribute("data-rpe-error");
    if (rpeError) {
      this.context.locals.flash("error", rpeError);
      submit("/actions/refresh");
      return;
    }
    if (
      this.state.exercises === undefined ||
      this.state.exercises.length === 0
    ) {
      this.closeQuickEditMode();
      this.setState({ exercises: [] });
      submit("/actions/exercise/quick-edit/cancel");
    } else if (checkOtherExercise(this.state.exercises)) {
      submit("/actions/exercise/quick-edit?reqSource=calendar", {
        method: "POST",
        body: { exercises: this.state.exercises },
      });
      if (close) {
        this.closeQuickEditMode();
        this.setState({ exercises: [] });
      }
    }
  }

  openQuickEditNotes() {
    const workoutNote = this.context.locals.workoutNote;
    this.setState(
      {
        openQuickEditNotes: true,
        notes: workoutNote?.note?.note ? workoutNote.note.note : "",
      },
      () => {
        document.getElementById("notes-textarea")?.focus();
      }
    );
  }

  closeQuickEditNotes() {
    this.setState({
      openQuickEditNotes: false,
      sendToCoach: false,
    });
  }

  handleNoteChange(value) {
    this.setState({ notes: value });
  }

  handleCheckboxChange() {
    this.setState({ sendToCoach: !this.state.sendToCoach });
  }

  saveQuickEditNotes() {
    const { originalQueryDate } = this.context.locals;
    submit("/actions/exercise/quick-edit-note", {
      method: "POST",
      body: {
        note: this.state.notes,
        date: originalQueryDate,
        sendToCoach: this.state.sendToCoach,
      },
    });

    if (this.context.locals.workoutNote?.note?.note) {
      this.context.locals.workoutNote.note.note = this.state.notes;
    } else {
      this.context.locals.workoutNote = {
        note: { note: this.state.notes },
      };
    }

    this.closeQuickEditNotes();
  }

  renderBodyPhotos(activeDay = null) {
    const {
      locals: { dailyStats, query },
    } = this.context;
    const existPhotos =
      dailyStats.find(
        (stat) => stat.bodyPhotos && activeDay.isSame(stat.date)
      ) || false;
    if (!existPhotos) return null;
    const { athlete } = query;
    const urlStart = athlete ? "/athlete-portal/metrics" : "/dashboard/metrics";
    const urlEnd = athlete ? `&athlete=${athlete}` : "";
    const date = activeDay.format(DATE_DAY_FORMAT);
    return (
      <div className="progress-photo-wrapper">
        <span>{this.props.t("calendar.calendarDay.progressPhotos")}</span>
        <div className="photos-container">
          {["front", "back", "side"].map((photoType) => {
            const pictureSrc = existPhotos.bodyPhotos[photoType] || null;
            return (
              <Picture
                key={photoType}
                src={pictureSrc ? pictureSrc.s3Path : pictureSrc}
                toggleModal={this.toggleModal}
                className="body-photo"
                photoType={photoType}
                loaded
              />
            );
          })}
        </div>
        <a
          href={`${urlStart}/progress-photos?view=day&date=${date}${urlEnd}`}
          className="see-all"
        >
          {this.props.t("calendar.calendarDay.seeAll")}
        </a>
      </div>
    );
  }

  renderNotes({ notes, coachNotes, athleteInfo }) {
    return (
      <div className="notes-container">
        <div className="athlete-notes">
          <span className="notes-heading">
            {this.props.t("calendar.calendarDay.notes")}
          </span>
          {!this.state.openQuickEditNotes ? (
            <button
              type="button"
              className="edit-mode w-full text-left"
              onClick={() => this.openQuickEditNotes()}
            >
              {notes.length && notes.trim().length
                ? notes
                : this.props.t("calendar.calendarDay.clickToAddEditNotes")}
            </button>
          ) : (
            <div className="edit-mode editing">
              <textarea
                value={this.state.notes || ""}
                onChange={({ target: { value } }) =>
                  this.handleNoteChange(value)
                }
                id="notes-textarea"
              />
              <div className="button-container">
                <button
                  data-button-type="primary"
                  onClick={() => this.saveQuickEditNotes()}
                  type="button"
                >
                  {this.props.t("calendar.calendarDay.save")}
                </button>
                <button
                  data-button-type="secondary"
                  onClick={() => this.closeQuickEditNotes()}
                  type="button"
                >
                  <SvgIcon
                    src="/img/new-icons/red-cross.svg"
                    noFilterOnLight={true}
                  />
                </button>
              </div>
            </div>
          )}
        </div>
        {athleteInfo.coach && coachNotes && coachNotes.trim().length ? (
          <div className="coach-notesx">
            <span className="notes-heading">
              {this.props.t("calendar.calendarDay.coachNotes")}
            </span>
            <div className="edit-mode cursor-default!">
              <span>{coachNotes}</span>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  render() {
    const { locals } = this.context;
    const {
      query,
      activeExercises,
      editExercises,
      editCycle,
      activeUser,
      activeUserToRequest,
      activeUser: { isCoachEditing },
      cycleDetail = {},
      workoutNote,
      workoutCoachNote,
      dailyStats,
      pathname,
    } = locals;
    const user = this.user;
    const notes = workoutNote?.note?.note ? workoutNote.note.note : "";

    const coachNotes = workoutCoachNote?.note?.note
      ? workoutCoachNote.note.note
      : "";

    const nav = query.nav || "0";

    const program = lodashFind(lodashGet(editCycle, "programs", []), {
      order: cycleDetail.program,
    });
    const sessionOrderInProgram = program
      ? getSessionOrder(
          program,
          cycleDetail.programWeek,
          cycleDetail.programSession
        )
      : undefined;
    const session = lodashFind(lodashGet(program, "sessions", []), {
      order: sessionOrderInProgram,
    });

    const activeDay = moment(query.date, DATE_DAY_FORMAT);
    const dayStats = getActiveDayStats({
      activeDayUtc: parseInt(activeDay.format(UTC), 10),
      stats: dailyStats,
    });

    const { photoModal, openQuickEditNotes } = this.state;

    return (
      <main className="Calendar">
        <TopBarSingleDay query={query} activeDay={activeDay} />

        <section className="Calendar-Day">
          {editCycle && (
            <SingleDayCycleBar
              exerciseCycleDetails={cycleDetail}
              cycle={editCycle}
              cycleExercises={editExercises}
              exercises={activeExercises}
              activeDay={activeDay}
              setSessionOrder={this.setSessionOrder}
              nav={nav}
              pathname={pathname}
            />
          )}

          <section className="Daily-Workout">
            <DaylyWorkoutHeader
              activeExercises={activeExercises}
              sessionOrderInProgram={sessionOrderInProgram}
              query={query}
              openQuickEditMode={this.openQuickEditMode}
              handleRemove={(e) => handleRemove(e, activeDay, this.props.t)}
              dailyStats={dayStats}
              activeUser={activeUser}
              activeUserToRequest={activeUserToRequest}
              activeDay={activeDay}
            />

            <ExerciseFrame
              activeExercises={activeExercises}
              sections={locals.activeSections || []}
              openQuickEdit={this.state.openQuickEdit}
              editCycle={editCycle}
              program={program}
              session={session}
              fromMetric={locals.fromMetric}
              activeUser={locals.activeUser}
              handleDuplicate={this.handleDuplicateExercise}
              openQuickEditMode={this.openQuickEditMode}
              handleQuickEditChange={this.handleQuickEditChange}
              handleAddSetLocally={this.handleAddSetLocally}
              loadInputs={this.loadInputs}
              intensityInputs={this.intensityInputs}
              query={query}
              saveQuickEdit={(e) => this.saveQuickEdit(e)}
              exercises={this.state.exercises}
              closeQuickEditMode={this.closeQuickEditMode}
              handleRemove={(e) => handleRemove(e, activeDay, this.props.t)}
            />
            <footer className="footer-notes">
              {this.renderNotes({ notes, coachNotes, athleteInfo: user })}
              {this.renderBodyPhotos(activeDay)}
            </footer>
          </section>
          <hr />
          <QuickMetrics locals={locals} />
        </section>

        {isCoachEditing && (
          <div className="Coach-Edit">
            <img src="/img/icons/edit.svg" width="20" height="20" alt="" />
            {this.props.t("calendar.calendarMonth.coachEditingMessage")}
          </div>
        )}
        {photoModal.open && !openQuickEditNotes ? (
          <PhotoModal toggleModal={this.toggleModal} photo={photoModal} />
        ) : null}
      </main>
    );
  }
}

View.contextType = RillContext;

const CalendarSingleDayView = withTranslation("dashboard")(View);

function handleRemove(e, date, t) {
  const message = t
    ? t("calendar.calendarDay.removeWorkoutConfirm")
    : "Are you sure you want to remove this workout?";
  if (confirm(message)) {
    e.preventDefault();

    submit(
      `/actions/exercise/unschedule-workout?date=[${date.format(
        DATE_DAY_FORMAT
      )}]`,
      {
        method: "POST",
        body: {
          days: [date.format(DATE_DAY_FORMAT)],
          source: "calendarDayView",
        },
      }
    );
  }
}

const CalendarSingleDay = (app) => {
  app.get("/", async ({ req, res, locals, load, api, fail }, next) => {
    if (req.query.view !== "day") return next();
    const { query } = req;
    const { units } = locals;
    const modal = lodashGet(query, "modal", {});

    lodashDefaults(query, {
      units,
      date: moment.utc(query.date).format(DATE_DAY_FORMAT),
      zoneType: "total",
    });

    locals.originalQueryDate = `${query.date}`;

    if (query.date instanceof Date)
      query.date = moment.utc(query.date).format(DATE_DAY_FORMAT);
    const startDate = moment.utc(query.date, DATE_DAY_FORMAT).startOf("day");
    const endDate = moment.utc(query.date, DATE_DAY_FORMAT).endOf("day");

    await Promise.all([
      load("activeExercises", {
        start: startDate,
        end: endDate,
      }),
      load("activeSections", {
        start: startDate,
        end: endDate,
      }),
      load("currentYearExercises", {
        start: startDate,
        end: endDate,
      }),
      load("workoutNote", locals.originalQueryDate),
      // Exact-day match: feeds PersonalRecordsCard (via QuickMetrics), which
      // has no date filtering of its own and expects only PRs literally set
      // on this specific day.
      load("dayPersonalRecords", {
        end: moment
          .utc(locals.query.date, DATE_DAY_FORMAT)
          .endOf("day")
          .format(UTC),
      }),
      // Wide range: feeds the badge-matching logic in OutcomesComponent. A
      // PR is recognized retroactively once enough history exists, so it
      // can only be found by range-matching against "today", not by
      // requiring its recorded date to equal this exact day.
      load("dayPersonalRecordsRange", {
        start: moment.utc("2017-01-01", UTC).format(UTC),
        end: moment
          .utc(locals.query.date, DATE_DAY_FORMAT)
          .endOf("day")
          .format(UTC),
        isCheckIn: true,
      }),
      load("dailyStats", {}),
      load("nutritionPlans", {}),
    ]);

    if (locals.activeUser.coach) {
      await load(
        "workoutCoachNote",
        locals.originalQueryDate,
        locals.activeUser._id,
        locals.activeUser.coach._id
      );
    }

    if (locals.activeExercises) {
      const exerciseWithCycle =
        locals.activeExercises.find((e) => e.cycle) || {};
      locals.cycleDetail = exerciseWithCycle.cycle;
      const cycleId = lodashGet(locals, "cycleDetail.id", false);
      if (cycleId)
        await Promise.all([
          load("editCycle", {
            cycle: cycleId,
            proxyAthlete: query.athlete,
          }),
          load("editExercises", {
            cycle: cycleId,
            isScheduled: true,
            proxyAthlete: query.athlete,
          }),
        ]);
    }
    if (modal.type === "share" && !modal.snapshot) {
      // create share link
      try {
        modal.snapshot = (
          await (
            await api("snapshot", {
              method: "POST",
              body: {
                type: "workout",
                data: JSON.stringify(
                  lodashChain(locals)
                    .pick(["activeExercises"])
                    .assign({
                      start: startDate.toJSON(),
                      end: endDate.toJSON(),
                    })
                    .value()
                ),
              },
            })
          ).json()
        )._id;

        modal.date = getDayRangeString([startDate, endDate]);

        return res.refreshWith({ modal });
      } catch (e) {
        console.error(e.stack || e);
        fail(400, "Error creating share link.");
      }
    }

    const editExercise = query.editMode
      ? lodashFind(locals.activeExercises, { _id: query.exercise })
      : {};

    // Resolve threadId from exercise video when opening exercise-set-video modal without threadId in URL (e.g. from activity "uploaded-video-set")
    let resolvedThreadId = query.threadId;
    if (
      modal.type === "exercise-set-video" &&
      !resolvedThreadId &&
      query.exerciseToEdit != null &&
      query.setIndex != null &&
      query.videoIndex != null &&
      Array.isArray(locals.activeExercises)
    ) {
      const exercise = lodashFind(
        locals.activeExercises,
        (e) => String(e._id) === String(query.exerciseToEdit)
      );
      const video = lodashGet(
        exercise,
        ["sets", Number(query.setIndex), "videos", String(query.videoIndex)],
        null
      );

      if (video) {
        resolvedThreadId = video.threadId || video.thread?._id;
      }
    }

    if (resolvedThreadId) {
      await load("comments", {
        threadId: resolvedThreadId,
      });
    } else {
      delete locals.comments;
    }

    // Build prev/next sequence for exercise-set-video modal
    let prevHref;
    let nextHref;
    if (
      modal.type === "exercise-set-video" &&
      Array.isArray(locals.activeExercises)
    ) {
      const sequence = [];
      for (const exercise of locals.activeExercises) {
        const exerciseId = String(exercise._id);
        const primary = exercise.primary ?? "";
        const secondary = exercise.secondary ?? "";
        const type = exercise.type ?? "";
        const movement = exercise.movement ?? "";
        (exercise.sets || []).forEach((set, setIdx) => {
          const videos = set.videos || {};
          Object.keys(videos)
            .map(Number)
            .sort((a, b) => a - b)
            .forEach((vidIdx) => {
              const video = videos[vidIdx];
              if (!video?.videoOriginal) return;
              const seqOutcomeObj = set.outcomes?.[vidIdx]?.outcome || {};
              const seqIsPowerlifting =
                exercise.type === "primary" || exercise.type === "secondary";
              const { units, intensityMeasure } = locals.activeUser;
              const seqPrecision = units === "metric" ? 2 : 1;
              const seqUserMetrics = units === "imperial" ? "lb" : "kg";
              const seqRawLoad = seqOutcomeObj.load;
              const seqLoad = seqRawLoad
                ? `${lodashRound(locals.fromMetric(seqRawLoad, units), seqPrecision)} ${seqUserMetrics}`
                : 0;
              const seqTarget =
                seqRawLoad || seqOutcomeObj.reps || seqOutcomeObj.rpe
                  ? formattedTarget(
                      seqOutcomeObj,
                      seqIsPowerlifting,
                      set.percentRepMax || undefined,
                      units,
                      seqLoad,
                      intensityMeasure,
                      intensityMeasure !== "rpe",
                      true
                    ) || undefined
                  : undefined;
              const seqSetNumber = setIdx + 1;
              sequence.push({
                key: `${exerciseId}:${setIdx}:${vidIdx}`,
                href: buildVideoModalUrl({
                  modalType: "exercise-set-video",
                  video,
                  exerciseToEdit: exerciseId,
                  setIndex: setIdx,
                  videoIndex: vidIdx,
                  utcDate: exercise.utcDate,
                  primary,
                  secondary,
                  type,
                  movement,
                  setNumber: seqSetNumber,
                  setTarget: seqTarget,
                  setComment: set.comments?.[vidIdx] || undefined,
                }),
              });
            });
        });
      }
      const currentKey = `${query.exerciseToEdit}:${Number(query.setIndex)}:${Number(query.videoIndex)}`;
      ({ prevHref, nextHref } = findVideoNeighbors({ sequence, currentKey }));
    }

    locals.modal = {
      "exercise-set-video": (
        <JWPlayerVideoModal
          athleteId={query.uploaderId || locals.activeUser._id}
          exerciseToEdit={query.exerciseToEdit}
          locals={locals}
          mediaId={query.mediaId}
          movement={query.movement}
          originalId={query.originalId}
          primary={query.primary}
          reviewedByTheCoach={query.reviewedByTheCoach}
          secondary={query.secondary}
          setIndex={query.setIndex}
          threadId={resolvedThreadId}
          type={query.type}
          utcDate={query.utcDate}
          videoIndex={query.videoIndex}
          prevHref={prevHref}
          nextHref={nextHref}
          setNumber={query.setNumber}
          setTarget={query.setTarget}
          setComment={query.setComment}
        />
      ),
      "calendar-add-exercise": (
        <CalendarAddExerciseModal editExercise={editExercise} />
      ),
      share: <ShareModal />,
      progression: <ProgressionModal />,
      "swap-exercise": <SwapExerciseModal exercise={editExercise} />,
    }[modal.type];
    res.body = <CalendarSingleDayView />;
  });
};

export default CalendarSingleDay;
